import { Button, Heading, Input, Modal, Switch, Text } from "@ttc3k/trekker";
import { Suspense, useEffect, useState } from "react";
import {
  TextEditor,
  isLexicalHtmlEmpty,
} from "shared/components/molecules/TextEditor";
import { Flex, Grid } from "styled-system/jsx";
import {
  RenewalSendTestEmailModal,
  type RenewalEmailKind,
} from "./RenewalSendTestEmailModal";

type RenewalEmailFlyoutProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  emailKind: RenewalEmailKind;
  title: string;
  subjectValue: string;
  bodyValue: string;
  subjectPlaceholder: string;
  bodyPlaceholder: string;
  subjectLabel: string;
  bodyLabel: string;
  saveLabel: string;
  cancelLabel: string;
  sendTestLabel: string;
  hideRenewalDateLineValue?: boolean;
  hideRenewalDateLineLabel?: string;
  greetingPreviewLabel?: string;
  /** Saved button label: undefined/null follows the default, "" means no button. */
  ctaLabelValue?: string | null;
  /** The email's default button label ("" when it has none by default). */
  defaultCtaLabel?: string;
  ctaLabelLabel?: string;
  ctaLabelHint?: string;
  onSave: (
    subject: string,
    body: string,
    hideRenewalDateLine: boolean,
    /** null = follow the default, "" = no button, otherwise the custom label. */
    ctaLabel: string | null,
  ) => void;
  isSaving?: boolean;
};

const resolveInitialCtaLabel = (
  saved: string | null | undefined,
  fallback: string,
) => (typeof saved === "string" ? saved : fallback);

export const RenewalEmailFlyout = ({
  isOpen,
  onClose,
  organizationId,
  emailKind,
  title,
  subjectValue,
  bodyValue,
  subjectPlaceholder,
  bodyPlaceholder,
  subjectLabel,
  bodyLabel,
  saveLabel,
  cancelLabel,
  sendTestLabel,
  hideRenewalDateLineValue = false,
  hideRenewalDateLineLabel,
  greetingPreviewLabel,
  ctaLabelValue,
  defaultCtaLabel = "",
  ctaLabelLabel,
  ctaLabelHint,
  onSave,
  isSaving,
}: RenewalEmailFlyoutProps) => {
  const [subject, setSubject] = useState(subjectValue);
  const [body, setBody] = useState(bodyValue);
  const [hideRenewalDateLine, setHideRenewalDateLine] = useState(
    hideRenewalDateLineValue,
  );
  const initialCtaLabel = resolveInitialCtaLabel(ctaLabelValue, defaultCtaLabel);
  const [ctaLabel, setCtaLabel] = useState(initialCtaLabel);
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubject(subjectValue);
      setBody(bodyValue);
      setHideRenewalDateLine(hideRenewalDateLineValue);
      setCtaLabel(initialCtaLabel);
      setShowTestModal(false);
    }
  }, [isOpen, subjectValue, bodyValue, hideRenewalDateLineValue, initialCtaLabel]);

  const normalizedBody = isLexicalHtmlEmpty(body) ? "" : body;
  const trimmedCtaLabel = ctaLabel.trim();
  const hasChanges =
    subject !== subjectValue ||
    normalizedBody !== bodyValue ||
    (!!ctaLabelLabel && trimmedCtaLabel !== initialCtaLabel.trim()) ||
    (!!hideRenewalDateLineLabel &&
      hideRenewalDateLine !== hideRenewalDateLineValue);

  const handleSave = () => {
    // Saving the default text keeps following the default if it changes.
    const savedCtaLabel =
      !ctaLabelLabel || trimmedCtaLabel === defaultCtaLabel
        ? null
        : trimmedCtaLabel;
    onSave(subject, normalizedBody, hideRenewalDateLine, savedCtaLabel);
  };

  return (
    <>
      <Modal
        open={isOpen}
        placement="rightLargePane"
        onOpenChange={onClose}
        closeTriggerVisual="light"
        portal
      >
        <Grid
          gridTemplateRows="minmax(0, 1fr) auto"
          h="full"
          gap="0px"
          overflow="hidden"
        >
          <Flex
            flexDirection="column"
            minH={0}
            py="600"
            px="500"
            overflowY="auto"
            gap="400"
          >
            <Flex
              flexDirection="column"
              pb="250"
              borderBottomWidth="1px"
              borderStyle="solid"
              borderColor="border.light"
            >
              <Heading size="h4" color="text.dark">
                {title}
              </Heading>
            </Flex>

            <Input
              label={subjectLabel}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder={subjectPlaceholder}
              disabled={isSaving}
            />

            {greetingPreviewLabel && (
              <Text visual="bodyRegular" color="text.mid">
                {greetingPreviewLabel}
              </Text>
            )}

            {hideRenewalDateLineLabel && (
              <Flex alignItems="flex-start" gap="200">
                <Switch
                  checked={hideRenewalDateLine}
                  onCheckedChange={event =>
                    setHideRenewalDateLine(event.checked)
                  }
                  disabled={isSaving}
                />
                <Text visual="bodyRegular" color="text.mid">
                  {hideRenewalDateLineLabel}
                </Text>
              </Flex>
            )}

            <Flex flexDir="column" gap="100">
              <Text visual="smallSemiBold">{bodyLabel}</Text>
              <Suspense fallback={null}>
                <TextEditor
                  value={body}
                  onChange={setBody}
                  placeholder={bodyPlaceholder}
                />
              </Suspense>
            </Flex>

            {ctaLabelLabel && (
              <Input
                label={ctaLabelLabel}
                hint={ctaLabelHint}
                value={ctaLabel}
                onChange={e => setCtaLabel(e.target.value)}
                placeholder={defaultCtaLabel}
                maxLength={60}
                disabled={isSaving}
              />
            )}
          </Flex>

          <Flex
            px="500"
            py="300"
            borderTopWidth="1px"
            borderColor="border.light"
            borderStyle="solid"
            boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.10)"
            justifyContent="space-between"
            alignItems="center"
            gap="150"
          >
            <Flex gap="150">
              <Button mode="light" onClick={onClose} disabled={isSaving}>
                {cancelLabel}
              </Button>
              <Button
                mode="light"
                onClick={() => setShowTestModal(true)}
                disabled={isSaving || !organizationId}
              >
                {sendTestLabel}
              </Button>
            </Flex>
            <Button
              visual="accent.blue"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {saveLabel}
            </Button>
          </Flex>
        </Grid>
      </Modal>

      <RenewalSendTestEmailModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        organizationId={organizationId}
        emailKind={emailKind}
        subject={subject}
        body={normalizedBody}
        hideRenewalDateLine={hideRenewalDateLine}
        ctaLabel={ctaLabelLabel ? trimmedCtaLabel : undefined}
      />
    </>
  );
};
